import { useContext } from "react";
import SyllabusSelect from "../../components/SyllabusSelect";
import DateChoice from "../../components/DateChoice";
import EditSyllabusButton from "../../components/EditSyllabusButton";
import { ChevronRight } from "lucide-react";
import { UserContext } from "../../../../Context";
import { usePrimaryColor } from "../../components/PrimaryColorContext";


/**
 * ClassSettings Component
 * Displays class settings in compact horizontal card.
 * Streamlined to show only syllabus and level information.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.htClass - The class object
 * @param {Function} props.setHtClass - Function to update class object
 * @param {string} props.classType - Type of class
 * @param {string} props.unitId - Unit ID
 * @param {string} props.lessonId - Lesson ID
 *
 * @author Forge
 * @date 2026-04-10
 * @version 4.1.0
 */
export default function ClassSettings({
  htClass,
  setHtClass,
  classType,
  unitId,
  lessonId,
}) {
  const { user } = useContext(UserContext);
  const { primaryColor } = usePrimaryColor();
  
  const canEditSyllabus = user?.roles?.includes("Manager") || user?.roles?.includes("Head Teacher");

  const handleSyllabusChange = (syllabus) => {
    setHtClass({ ...htClass, syllabus });
  };

  const handleNextSyllabusChange = (nextSyllabus) => {
    setHtClass({ ...htClass, nextSyllabus });
  };

  const handleMoveToNextLevelChange = (moveToNextLevel) => {
    setHtClass({ ...htClass, moveToNextLevel });
  };

  return (
    <div className="flex-1 min-w-[280px] bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
        <h3 className="text-base font-black text-slate-900 flex items-center gap-2 uppercase tracking-tight">
          <ChevronRight size={16} className={`text-${primaryColor}-800 rotate-90`} /> Settings
        </h3>
      </div>

      {/* Settings List */}
      <div className="p-3 flex flex-col gap-3">
        {/* Current Syllabus with Edit Button */}
        <div className="flex flex-col gap-1">
          <label className="text-[0.6rem] font-black text-slate-400 uppercase tracking-wider mb-1">
            Current Syllabus
          </label>
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <SyllabusSelect
                value={htClass.syllabus}
                setValue={handleSyllabusChange}
                url={`/htclasses/${htClass._id}`}
                field="syllabus"
                size="small"
              />
            </div>
            {classType !== "undefined" && canEditSyllabus && (
              <div className="flex-shrink-0 pt-1">
                <EditSyllabusButton
                  syllabus={htClass.syllabus}
                  unit={unitId}
                  lesson={lessonId}
                />
              </div>
            )}
          </div>
        </div>

        {/* Move to Next Level */}
        <div className="flex flex-col gap-1">
          <DateChoice
            value={htClass.moveToNextLevel}
            setValue={handleMoveToNextLevelChange}
            label="Move to next level"
            field="moveToNextLevel"
            url={`/htclasses/${htClass._id}`}
            fullWidth
          />
        </div>

        {/* Next Syllabus */}
        <div className="flex flex-col gap-1">
          <SyllabusSelect
            label="Next Syllabus"
            value={htClass.nextSyllabus}
            setValue={handleNextSyllabusChange}
            url={`/htclasses/${htClass._id}`}
            field="nextSyllabus"
            size="small"
          />
        </div>
      </div>
    </div>
  );
}